import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  HelpCircle,
  X,
  UserPlus,
  Users,
  Network,
  Split,
  Lightbulb,
  Sparkles,
  Mail,
  ArrowRight,
  ArrowLeft,
  CheckCircle2,
} from 'lucide-react';
import { GUIDE_UPDATES, type GuideUpdateTag } from '@/data/guideUpdates';
import FeatureGuide, { type FeatureGuideAction } from './FeatureGuide';

/**
 * GuideModal
 * ─────────────────────────────────────────────────────────────────────────
 * The single, always-available "How it works" guide for KinsCircle.
 *
 * Opens when:
 *   • The user clicks the "Guide" button in the top header (any page).
 *   • Any code in the app dispatches `window.dispatchEvent(new Event('kc:open-help'))`.
 *   • Any code dispatches a CustomEvent `kc:open-help` with
 *     `detail: { tab: 'features' | 'how' | 'updates' | 'tour' }` to land
 *     directly on a specific tab (used by the hamburger "Feature Guide" item).
 *
 * Contents:
 *   1. Tab "Feature Guide" — searchable Help Center listing every major
 *      feature with an action button and expandable "How it works" panel.
 *   2. Tab "How it works"  — five sections (A–E) with the canonical
 *      onboarding instructions: Getting Started, Adding Family,
 *      Understanding the Tree, Branch Separation, and Tips.
 *   3. Tab "What's New"    — reads `GUIDE_UPDATES` from `src/data/guideUpdates.ts`.
 *      Editing that file is the only step required to publish a new update;
 *      no rebuild, no backend, no migration.
 *   4. Tab "First-time tour" — a tiny 3-step overlay (Add yourself & parents →
 *      Add close family → Invite family) so brand-new users have a clear path.
 *
 * NOTE: This component is intentionally read-only. It does NOT touch tree
 * logic, permissions, the database, or any other UI surface. Action
 * buttons inside the Feature Guide tab delegate to the parent via the
 * onScrollTo / onOpenAccount / onContinueSetup / onInviteFamily props.
 */

type TabKey = 'features' | 'how' | 'updates' | 'tour';

interface GuideModalProps {
  /** Optional controlled open state. If omitted, the modal listens for the
   *  `kc:open-help` window event and manages its own open state. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Optional CTA bridge — when present, the "Invite family" tour step
   *  and Feature Guide invite cards will trigger this instead of just closing. */
  onInviteFamily?: () => void;
  /** Scroll the underlying page to a known section id (e.g. 'graph',
   *  'gallery'). Used by Feature Guide action buttons. */
  onScrollTo?: (sectionId: string) => void;
  /** Navigate to the account / settings page. */
  onOpenAccount?: () => void;
  /** Re-open the first-time tour / continue the onboarding flow. */
  onContinueSetup?: () => void;
}


const TAG_STYLES: Record<GuideUpdateTag, string> = {
  new: 'bg-fuchsia-100 text-fuchsia-700 border-fuchsia-200',
  fix: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  improved: 'bg-amber-100 text-amber-800 border-amber-200',
};
const TAG_LABEL: Record<GuideUpdateTag, string> = {
  new: 'New',
  fix: 'Fix',
  improved: 'Improved',
};

const formatDate = (iso: string) => {
  // Render as "May 3, 2026" without pulling in date-fns.
  try {
    const d = new Date(iso + 'T00:00:00');
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
};

const SECTIONS = [
  {
    id: 'a',
    letter: 'A',
    title: 'Getting Started',
    Icon: UserPlus,
    tone: 'from-fuchsia-500 to-pink-500',
    bullets: [
      'Add yourself first — this anchors your tree.',
      'Add your parents next — they create the foundation above you.',
      "From there, every other relative connects through your parents or your children.",
    ],
  },
  {
    id: 'b',
    letter: 'B',
    title: 'Adding Family',
    Icon: Users,
    tone: 'from-amber-500 to-orange-500',
    bullets: [
      'Tap any person and choose how the new person is related (parent, child, sibling, spouse).',
      'KinsCircle places them in the correct generation automatically.',
      'You never need to drag, position, or re-arrange anyone manually.',
    ],
  },
  {
    id: 'c',
    letter: 'C',
    title: 'Understanding the Tree',
    Icon: Network,
    tone: 'from-purple-500 to-fuchsia-600',
    bullets: [
      'Your tree is organized by generations — oldest at the top, newest at the bottom.',
      'Parents always sit above their children; children always sit below their parents.',
      'Each side of the family stays separate unless they connect through a shared child.',
    ],
  },
  {
    id: 'd',
    letter: 'D',
    title: 'Branch Separation',
    Icon: Split,
    tone: 'from-emerald-500 to-teal-500',
    bullets: [
      'You only see and manage your side of the family — your direct lineage.',
      "The other parent's extended family is controlled by their own members.",
      'Branches are joined only through shared children, never merged or overlapped.',
    ],
  },
  {
    id: 'e',
    letter: 'E',
    title: 'Tips',
    Icon: Lightbulb,
    tone: 'from-sky-500 to-indigo-500',
    bullets: [
      'Always connect a new person through a parent for the cleanest structure.',
      'Avoid duplicate entries — search before adding to see if the person is already in the tree.',
      'Invite relatives to claim their own card so they can manage their side themselves.',
    ],
  },
] as const;

const TOUR_STEPS = [
  {
    n: 1,
    title: 'Add yourself and your parents',
    body:
      'This is the foundation. Once you and your parents are in, every other relative connects naturally through them.',
    Icon: UserPlus,
    tone: 'from-fuchsia-500 to-pink-500',
  },
  {
    n: 2,
    title: 'Add close family',
    body:
      'Add siblings, your spouse, your children, and your grandparents. Tap any card and choose how the new person is related — KinsCircle places them automatically.',
    Icon: Users,
    tone: 'from-amber-500 to-orange-500',
  },
  {
    n: 3,
    title: 'Invite your family',
    body:
      'Send a private link so each relative can claim their card. Each person manages their own side of the family from there.',
    Icon: Mail,
    tone: 'from-emerald-500 to-teal-500',
  },
] as const;

const GuideModal: React.FC<GuideModalProps> = ({
  open: controlledOpen,
  onOpenChange,
  onInviteFamily,
  onScrollTo,
  onOpenAccount,
  onContinueSetup,
}) => {
  // Self-managed open state when not driven externally.
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = typeof controlledOpen === 'boolean';
  const open = isControlled ? !!controlledOpen : internalOpen;
  const setOpen = (o: boolean) => {
    if (isControlled) onOpenChange?.(o);
    else setInternalOpen(o);
  };

  // Default landing tab is the new searchable Feature Guide so the
  // hamburger "Feature Guide" entry and the header "Guide" button both
  // open users straight into the most useful surface.
  const [tab, setTab] = useState<TabKey>('features');
  const [tourIdx, setTourIdx] = useState(0);

  // Listen for the global `kc:open-help` event so any button anywhere in
  // the app can pop the guide without prop drilling. Supports an optional
  // CustomEvent `detail.tab` payload so the hamburger "Feature Guide"
  // item can land directly on the Feature Guide tab.
  useEffect(() => {
    const handler = (e: Event) => {
      setOpen(true);
      const detail = (e as CustomEvent<{ tab?: TabKey }>).detail;
      const requested = detail?.tab;
      if (
        requested === 'features' ||
        requested === 'how' ||
        requested === 'updates' ||
        requested === 'tour'
      ) {
        setTab(requested);
      } else {
        setTab('features');
      }
    };
    window.addEventListener('kc:open-help', handler as EventListener);
    return () =>
      window.removeEventListener('kc:open-help', handler as EventListener);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reset the tour pip every time the modal opens fresh.
  useEffect(() => {
    if (open) {
      setTourIdx(0);
    }
  }, [open]);

  // Translate a Feature Guide action into a real, existing app surface.
  // Every branch reuses an EXISTING route or scroll target — there are no
  // dead buttons. We close the modal first so the underlying scroll/nav
  // is visible to the user.
  const handleFeatureAction = (action: FeatureGuideAction) => {
    setOpen(false);
    window.setTimeout(() => {
      switch (action) {
        case 'tree':
        case 'feed':
          // Family Activity Feed lives near the graph section on the
          // dashboard, so 'graph' is the closest existing scroll target.
          onScrollTo?.('graph');
          break;
        case 'gallery':
          onScrollTo?.('gallery');
          break;
        case 'invite':
          onInviteFamily?.();
          break;
        case 'account':
          onOpenAccount?.();
          break;
        case 'tour':
          if (onContinueSetup) onContinueSetup();
          else {
            // Fallback — re-open this same modal on the first-time tour tab.
            setOpen(true);
            setTab('tour');
          }
          break;
        default:
          break;
      }
    }, 80);
  };


  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        className="max-w-2xl p-0 overflow-hidden rounded-2xl bg-white max-h-[90vh] flex flex-col"
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogTitle className="sr-only">KinsCircle Guide</DialogTitle>
        <DialogDescription className="sr-only">
          Learn how KinsCircle works, see what's new, and follow the
          first-time tour.
        </DialogDescription>

        {/* Header */}
        <div className="bg-gradient-to-br from-fuchsia-50 via-pink-50 to-amber-50 px-6 pt-5 pb-3 border-b border-fuchsia-100 shrink-0">
          <div className="flex items-center justify-between">
            <div className="inline-flex items-center gap-2">
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-fuchsia-500 to-pink-500 flex items-center justify-center shadow-md">
                <HelpCircle className="w-5 h-5 text-white" />
              </div>
              <div>
                <div className="font-serif text-lg font-bold text-slate-900 leading-tight">
                  KinsCircle Guide
                </div>
                <div className="text-[11px] text-slate-500">
                  How it works · what's new · first-time tour
                </div>
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              aria-label="Close guide"
              className="p-1.5 rounded-full hover:bg-white/70 text-slate-600"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Tab strip */}
          <div className="mt-4 flex items-center gap-1 bg-white/70 border border-fuchsia-100 rounded-full p-1 overflow-x-auto">
            {(
              [
                { key: 'features', label: 'Feature Guide' },
                { key: 'how', label: 'How it works' },
                { key: 'updates', label: "What's New" },
                { key: 'tour', label: 'First-time tour' },
              ] as { key: TabKey; label: string }[]
            ).map((t) => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`flex-1 min-w-max px-3 py-1.5 rounded-full text-xs sm:text-sm font-semibold transition-colors whitespace-nowrap ${
                  tab === t.key
                    ? 'bg-gradient-to-r from-fuchsia-600 to-pink-500 text-white shadow'
                    : 'text-slate-600 hover:bg-white'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* Body — scrollable */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {tab === 'features' && (
            <FeatureGuide onAction={handleFeatureAction} />
          )}


          {tab === 'how' && (
            <div className="space-y-4">
              <p className="text-sm text-slate-600">
                Five short sections explain everything you need to know to
                build, share, and grow your family circle.
              </p>
              {SECTIONS.map((s) => {
                const Icon = s.Icon;
                return (
                  <section
                    key={s.id}
                    className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 shadow-sm"
                  >
                    <div className="flex items-start gap-3">
                      <div
                        className={`shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br ${s.tone} flex items-center justify-center shadow-md`}
                      >
                        <Icon className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[11px] font-bold uppercase tracking-widest text-fuchsia-700">
                          Section {s.letter}
                        </div>
                        <h3 className="font-serif text-base sm:text-lg font-bold text-slate-900 mt-0.5">
                          {s.title}
                        </h3>
                        <ul className="mt-2 space-y-1.5">
                          {s.bullets.map((b, i) => (
                            <li
                              key={i}
                              className="flex items-start gap-2 text-sm text-slate-700"
                            >
                              <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                              <span>{b}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </section>
                );
              })}
            </div>
          )}

          {tab === 'updates' && (
            <div className="space-y-3">
              <p className="text-sm text-slate-600">
                Recent improvements, fixes, and new features. The newest
                update is shown first.
              </p>
              {GUIDE_UPDATES.length === 0 && (
                <div className="text-sm text-slate-500 italic">
                  No updates yet — check back soon.
                </div>
              )}
              {GUIDE_UPDATES.map((u, i) => (
                <article
                  key={`${u.date}-${i}`}
                  className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"
                >
                  <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wide border ${TAG_STYLES[u.tag]}`}
                    >
                      {TAG_LABEL[u.tag]}
                    </span>
                    <span className="text-[11px] text-slate-500 font-semibold">
                      {formatDate(u.date)}
                    </span>
                  </div>
                  <h4 className="font-semibold text-slate-900 text-sm sm:text-base">
                    {u.title}
                  </h4>
                  <p className="text-sm text-slate-600 mt-1 leading-relaxed">
                    {u.body}
                  </p>
                </article>
              ))}
            </div>
          )}

          {tab === 'tour' && (
            <div>
              <p className="text-sm text-slate-600 mb-4">
                Three short steps to start your family circle. You can
                re-open this tour any time from the Guide button.
              </p>

              {(() => {
                const step = TOUR_STEPS[tourIdx];
                const Icon = step.Icon;
                return (
                  <div className="rounded-2xl border border-fuchsia-100 bg-gradient-to-br from-white to-fuchsia-50/50 p-5 shadow-sm">
                    <div className="flex items-start gap-4">
                      <div
                        className={`shrink-0 w-12 h-12 rounded-2xl bg-gradient-to-br ${step.tone} flex items-center justify-center shadow-md`}
                      >
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">
                          Step {step.n} of {TOUR_STEPS.length}
                        </div>
                        <h4 className="font-serif text-lg font-bold text-slate-900 mt-0.5">
                          {step.title}
                        </h4>
                        <p className="text-sm text-slate-700 mt-2 leading-relaxed">
                          {step.body}
                        </p>
                      </div>
                    </div>

                    {/* Step pips */}
                    <div className="flex items-center gap-1.5 mt-5">
                      {TOUR_STEPS.map((_, i) => (
                        <button
                          key={i}
                          onClick={() => setTourIdx(i)}
                          aria-label={`Go to step ${i + 1}`}
                          className={`h-1.5 flex-1 rounded-full transition-colors ${
                            i === tourIdx ? 'bg-fuchsia-500' : 'bg-slate-200'
                          }`}
                        />
                      ))}
                    </div>

                    {/* Step nav */}
                    <div className="flex items-center justify-between mt-5">
                      <Button
                        variant="ghost"
                        onClick={() => setTourIdx((i) => Math.max(0, i - 1))}
                        disabled={tourIdx === 0}
                        className="rounded-full text-slate-600 gap-1"
                      >
                        <ArrowLeft className="w-4 h-4" />
                        Back
                      </Button>
                      {tourIdx < TOUR_STEPS.length - 1 ? (
                        <Button
                          onClick={() => setTourIdx((i) => i + 1)}
                          className="rounded-full bg-gradient-to-r from-fuchsia-600 to-pink-500 hover:from-fuchsia-700 hover:to-pink-600 text-white gap-1"
                        >
                          Next
                          <ArrowRight className="w-4 h-4" />
                        </Button>
                      ) : (
                        <Button
                          onClick={() => {
                            setOpen(false);
                            // Defer to let the dialog close before triggering
                            // the external invite-family CTA, if provided.
                            if (onInviteFamily) {
                              window.setTimeout(() => onInviteFamily(), 80);
                            }
                          }}
                          className="rounded-full bg-gradient-to-r from-emerald-600 to-teal-500 hover:from-emerald-700 hover:to-teal-600 text-white gap-1"
                        >
                          {onInviteFamily ? 'Invite family' : 'Done'}
                          <Sparkles className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })()}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-slate-100 bg-slate-50/60 flex items-center justify-between gap-2 shrink-0">
          <div className="text-[11px] text-slate-500">
            Tip: tap "Guide" at the top of any page to open this again.
          </div>
          <Button
            onClick={() => setOpen(false)}
            variant="outline"
            size="sm"
            className="rounded-full"
          >
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default GuideModal;
